import { QueryComponentsCollection } from "./../../search/collections/query-components";
import { IDocumentHolderUI } from "./../interfaces/document-holder-ui";
import { ViewMode } from "./document-state";
import { MobileHelper } from "./../../shared/helpers/mobile-helper";
import { ITabDefinition } from "./../interfaces/tab-definition";
import { GenericTabModel } from "./tabs/generic-tab";
import { DocumentEvents } from "./../helpers/document-events";
import { ReportDetailModel } from "./report-detail";
import { AppEvents } from "../../app-events";
import { IDocumentRequestOptions } from "./../interfaces/document-request-options";
import { JudgmentTabModel } from "./tabs/judgment";
import { PublisherModel } from "./publisher";
import { IDocumentModel } from "./../interfaces/document-model";
import { prop, DefaultOptions } from "../../decorators/property";
import { AuthorizationModel } from "../../authentication/models/authorization-model";
import { DocumentStateModel } from "../../desktop/models/document-state";
import { SiblingModel } from "../../desktop/models/stage";
import { GuidHelper } from "../../shared/helpers/guid-helper";
import { DocumentErrorHelper } from "../../shared/helpers/error-helper";
import { ParallelCitationModel } from "../../desktop/models/parallel-citation";
import { backboneModelProperties } from "../../decorators/backbone-properties";
import { transient, Container } from "aurelia-dependency-injection";
import { DocumentCategoriesModel } from "../../desktop/models/document-categories";
import { UserSettingsModel } from "../../settings/models/user-settings";
import { AppSettings } from "../../app-settings";
import { TabsCollection } from "../collections/tabs-collection";
import { ReportTabModel } from "./tabs/report";
import { FullTextDetailModel } from "./full-text-detail";
import { SearchModel } from "../../search/models/search";
import { QueryComponentModel } from "../../search/models/query-component";
import { AuthService } from "../../authentication/auth-service";
import { ContainerHelper } from "../../shared/helpers/container-helper";

@backboneModelProperties({
    idAttribute: "Id",
    relations : [{
        type: Backbone.Many,
        key: "AnalysisTabs",
        collectionType: TabsCollection
    },
    {
        type: Backbone.Many,
        key: "FulltextTabs",
        collectionType: TabsCollection
    },
    {
        type: Backbone.Many,
        key: "Siblings",
        relatedModel: SiblingModel
    }
   ],
    defaults: {
        AnalysisTabs: [],
        FulltextTabs: [],
        Siblings: []
    }
})
@transient()
export abstract class BaseDocumentModel extends AuthorizationModel implements IDocumentModel {
    public uistate: DocumentStateModel;
    public appEvents: Backbone.Events;
    public AnalysisTabs: TabsCollection = new TabsCollection();
    public FulltextTabs: TabsCollection = new TabsCollection();
    @prop() public TabDefinitions: ITabDefinition[];
    @prop() public Id: string;
    @prop() public Url: string;
    @prop() public UrlTemplate: string;
    @prop() public Siblings: Backbone.Collection<SiblingModel>;
    @prop() public Expired: boolean;
    @prop() public Populated: boolean;
    @prop() public Error: boolean;
    @prop() public Animated: boolean;
    @prop() public ParentId: string;
    @prop() public Title: string;
    @prop() public DocumentTitle: string;
    @prop() public FriendlyUrlTitle: string;
    @prop() public FullTextLoaded: boolean;
    @prop() public FullText: string;
    @prop() public SectionNumber: string;
    @prop() public ParallelCitations: Backbone.Collection<ParallelCitationModel>;
    @prop() public Publishers: Backbone.Collection<PublisherModel>;
    @prop() public ErrorMessage: string;
    @prop() public NoIndex: boolean;
    @prop() public PdfLinks: any[];
    @prop() public FaceWidth: number;
    @prop() public LastBatchId: string;
    @prop() public EntityId: any;
    @prop() public RenderType: string;
    @prop() public ShowYearByTitle: boolean;
    @prop(new DefaultOptions(GuidHelper.NewGuid())) public UniqueId: string;
    @prop() public DocCategoriesLoaded: boolean;
    @prop() public DocumentCategories: Backbone.Collection<any>;
    @prop() public DocumentAccessSku: string;
    @prop() public FullTextRendered: boolean;
    @prop() public HasPdf: boolean;
    @prop() public HasReports: boolean;
    @prop() public ReportDetails: Backbone.Collection<ReportDetailModel>;
    @prop() public FullTextDetails: Backbone.Collection<FullTextDetailModel>;
    @prop() public HasTranscripts: boolean;
    @prop() public ParagraphAndHighlightingControlsAllowed: boolean;
    @prop() public HasFullText: boolean;
    @prop() public Year: number;
    @prop() public Court: any;
    @prop() public IconUrl: string;
    @prop() public DisplayJurisdiction: string;
    @prop() public HasTopCategories: boolean;
    @prop() public TopCategories: string[];
    @prop() public ShowAllJurisdictions: boolean;
    @prop() public HighlightableSearchTerms: QueryComponentModel[];
    @prop() public RequestOptions: IDocumentRequestOptions;

    /** Flag to indicate if we want to load barebones data (e.g skip neutral cited cases). It defaults to true because it will only be applied if mobilemode=true */
    public mobileMinimalLoading = true;
    public mobileHelper: MobileHelper = Container.instance.get(MobileHelper);
    private authService: AuthService;
    private searchModel: SearchModel;

    constructor(options) {
        super(options);
        this.appEvents = ContainerHelper.Events;
        this.authService = ContainerHelper.AuthService;
        this.uistate = new DocumentStateModel(this);
        this.searchModel = Container.instance.get(SearchModel);
    }

    public get HasFullTextOrPdf(): boolean {
        return this.HasFullText || this.HasPdf;
    }

    public load(): Promise<IDocumentModel> {
        return new Promise<IDocumentModel>((resolve, reject) => {
            const skipNeutralAndUnclassifiedTreatments = this.mobileHelper.IsMobileMode && this.mobileMinimalLoading;
            if (this.Populated) {
                resolve(this);
            } else {
                this.fetch({
                    data: $.param({ type: this.RenderType, skipNeutralAndUnclassifiedTreatments: skipNeutralAndUnclassifiedTreatments }),
                    headers: {
                        UserSettings : this.getUserSettings()
                    }
                }).done(() => {
                    this.Error = false;
                    resolve(this);
                }).fail((response) => {
                    const errorMessage = DocumentErrorHelper.GetMessage(response.status);
                    this.Error = true;
                    reject(errorMessage);
                });
            }
        });
    }

    public abstract loadFullText(): Promise<{}>;

    public setupAppEventListeners(): void {
        this.appEvents.on(AppEvents.JurisdictionsSaved, () => {
            this.FullTextLoaded = false;
            this.refresh();
        });
    }

    public setUIStateOptions() {
        const options = this.RequestOptions;
        this.HighlightableSearchTerms = this.getHighlightableSearchTerms(this.searchModel.HighlightableSearchFragments);
        this.setActiveSections(options);
        if (this.FullTextLoaded) {
            this.setFullTextUIOptions(options);
        }
    }

    public checkDocHasOkPdfLink(pdfDetails: any[]) {
        let i = pdfDetails.length;
        let ok = false;
        while (i-- > 0) {
            if (pdfDetails[i].PdfStatus === 0) {
                ok = true;
            }
        }
        return ok;
    }

    public refresh(): Promise<IDocumentModel> {
        return new Promise<IDocumentModel>((resolve) => {
            this.fetch({
                data: $.param({ type: this.RenderType }),
            }).done((data) => {
                resolve(data);
                this.uistate.trigger(DocumentEvents.DocumentRefreshed);
            });
        });
    }

    public remove(): void {
        this.trigger("destroy", this);
    }

    public getUserSettings() {
        return JSON.stringify(UserSettingsModel.GetSettings());
    }

    public loadDocumentCategories() {
        return new Promise((resolve, reject) => {
            if (this.DocCategoriesLoaded) {
                resolve();
            } else {
                this.getDocumentCategoriesFromServer(this.Id).then((doc) => {
                    this.DocumentCategories = doc.Categories;
                    this.DocCategoriesLoaded = true;
                    resolve();
                });
            }
        });
    }

    public runPriorityProcess(data: any) {
        const options: any = {
            DocKeys: [data.Id],
            QueueName: "Priority"
        };
        const controllerName = this.getControllerNameFromProcess(data.processName);
        const url = AppSettings.ProcessingApiUrlPrefix + controllerName;

        if (controllerName.toLowerCase() === "patches") {
            options.Patch = data.processName;
        }
        return Promise.resolve($.ajax({
                url,
                type: "POST",
                data: options,
                success: (successData) => {
                    this.appEvents.trigger(AppEvents.InformationMessage, successData);
                },
                beforeSend: AuthorizationModel.attachAuthorizationHeader
         }));

    }

    protected getControllerNameFromProcess(process: string) {
        switch (process) {
            case "PatchCitingCounts":
                return "Patches";
            default:
                return process;
        }
    }

    protected getFulltextFromServer(docType: string, id: string): Promise<any> {
        return new Promise<string>((resolve, reject) => {
            $.ajax({
                beforeSend: AuthorizationModel.attachAuthorizationHeader,
                type: "GET",
                cache: false,
                url: `${AppSettings.DocumentApiUrlPrefix}document/fulltext/${docType}/${id}/0`,
            }).done(resolve).fail(reject);
        });
    }

    private getDocumentCategoriesFromServer(id: string): Promise<DocumentCategoriesModel> {
        return new Promise((resolve, reject) => {
            $.ajax({
                beforeSend: AuthorizationModel.attachAuthorizationHeader,
                type: "GET",
                cache: false,
                url: `${AppSettings.ApiUrlPrefix}document/categories/${id}`,
                success: (docCategories) => {
                    resolve(docCategories);
                }
            });
        });
    }

    private setActiveSections(options: IDocumentRequestOptions): void {
        if (!options) {
            return;
        }

        this.uistate.CurrentAnalysisSection = null;
        this.uistate.CurrentFulltextSection = null;
        this.uistate.CurrentAnalysisSection = this.getAnalysisSectionToActivate(options, this.AnalysisTabs);
        this.uistate.CurrentFulltextSection = this.getFullTextSectionToActivate(options, this.FulltextTabs);
    }

    private getAnalysisSectionToActivate(options: IDocumentRequestOptions, tabs: TabsCollection) {
        const matchingTab = tabs.findWhere({Identifier: options.section});
        if (matchingTab && matchingTab.Enabled) {
            return matchingTab;
        }

        return tabs.findWhere({Active: true});
    }

    private getFullTextSectionToActivate(options: IDocumentRequestOptions, tabs: TabsCollection) {
        const matchingTab = tabs.findWhere({Identifier: options.section});
        if (matchingTab && matchingTab.Enabled) {
            return matchingTab;
        }

        if (this.DocumentAccessSku !== "Pro" && this.DocumentAccessSku !== "Free") {
            if (this.ReportDetails && this.ReportDetails.length > 0) {
                return this.findFirstAvailableReportTab(tabs);
            } else {
                return tabs.last();
            }
        }

        if (this.HasFullTextOrPdf) {
            return tabs.findWhere({Active: true});
        } else if (!this.HasFullTextOrPdf && this.HasReports) {
            return this.findFirstAvailableReportTab(tabs);
        } else {
            return tabs.last();
        }
    }

    private findFirstAvailableReportTab(tabs: TabsCollection) {
        const reportSources = this.ReportDetails.pluck("Source") as string[];
        const firstAvailableSource = reportSources.find((source) => this.authService.AppUser.Indicators.indexOf(source) > -1);
        if (!firstAvailableSource) { // If user has no subscriptions to any of the document's report series then return 'Citations & Sources' tab.
            return tabs.last();
        }

        const firstAvailableTab = tabs.find((tab: ReportTabModel) => {
            if (tab.ReportDetails) {
                return tab.ReportDetails[0].Source === firstAvailableSource;
            }
        });
        return firstAvailableTab;
    }

    private setFullTextUIOptions(options: IDocumentRequestOptions) {
        if (options) {
            const section = options.section as string;
            const isReportSection = section.includes("report");
            this.uistate.resetViewMode();

            if (isReportSection) {
                this.uistate.showFulltext();
            } else {
                switch (section) {
                    case "fulltext-judgment":
                        this.uistate.showFulltext();
                        this.setJudgmentUIOptions(options);
                        break;
                    case "fulltext-statute-outline":
                    case "fulltext-legislation":
                    case "fulltext-sources":
                        this.uistate.showFulltext();
                        break;
                    default:
                        this.uistate.showAnalysis();
                        break;
                }
            }
        }
    }

    private setJudgmentUIOptions(options: IDocumentRequestOptions) {
        const judgmentTabModel = this.uistate.CurrentFulltextSection as JudgmentTabModel;
        if (judgmentTabModel) {
            judgmentTabModel.applyUIStateOptions(options);
        }
    }

    private getHighlightableSearchTerms(fragments: QueryComponentModel[]) {
        if (fragments) {
            return fragments.filter((fragment) => {
                return fragment.FieldName !== "MaterialType"
                && fragment.FieldName !== "JurisdictionId"
                && fragment.FieldName !== "CourtId"
                && fragment.FieldName !== "ReportSeries"
                && fragment.FieldName !== "Field";
            });
        }
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/models/base-document.ts